/**
 * Checkout Resource Client
 * Axios-based API client for checkout endpoints
 */

import type { OrderAddress } from '../types';
import { ApiClient } from './apiClient';

export type PaymentMethodName =
  | 'card' | 'paypal' | 'klarna' | 'apple_pay' | 'google_pay'
  | 'link' | 'bridge' | 'whop' | 'zelle' | 'hipay'
  | 'crypto' | 'convesiopay' | 'oceanpayment'
  | 'afterpay' | 'ideal' | 'bancontact' | 'giropay' | 'sepa_debit'
  | (string & {});

export interface CheckoutLineItem {
    externalProductId?: string | null;
    externalVariantId?: string | null;
    variantId?: string | null;
    priceId?: string | null;
    quantity: number;
}

export interface CheckoutInitParams {
    lineItems: CheckoutLineItem[];
    cartToken?: string;
    promotionIds?: string[];
    returnUrl?: string;
    draft?: boolean;
    useCustomDomain?: boolean;
    customerMetadata?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    storeId?: string;
    customerId?: string;
    customer?: {
        currency?: string;
        locale?: string;
    };
    enabledOrderBumpOfferIds?: string[];
}

export interface UpsellTrigger {
    id: string;
    type: string;
    productId: string | null;
}

export interface OrderBumpOfferVariant {
    id: string;
    name: string;
    sku: string;
    imageUrl: string;
    active: boolean;
    default: boolean;
    createdAt: string;
    updatedAt: string;
    externalVariantId: string | null;
    description: string;
    currency: string | null;
}

export interface OrderBumpOfferProduct {
    id: string;
    name: string;
}

export interface PriceCurrencyOption {
    rate: number;
    amount: number;
    lock: boolean;
    date: string;
}

export interface OrderBumpOfferPrice {
    id: string;
    currencyOptions: Record<string, PriceCurrencyOption>;
}

export interface CheckoutOrderBumpOffer {
    id: string;
    type: string;
    productId: string;
    variantId: string;
    priceId: string;
    titleTrans: Record<string, string>;
    descriptionTrans: Record<string, string>;
    overrideImageUrl: string | null;
    precheck: boolean;
    displayPrice: boolean;
    displayCompareAtPrice: boolean;
    compareAtPriceDiscount: number;
    quantity: number;
    variant: OrderBumpOfferVariant;
    product: OrderBumpOfferProduct;
    price: OrderBumpOfferPrice;
}

export interface Upsell {
    id: string;
    type: string;
    enabled: boolean;
    triggers: UpsellTrigger[];
    orderBumpOffers: CheckoutOrderBumpOffer[];
}

export interface Store {
    id: string;
    accountId: string;
    name: string;
    presentmentCurrencies: string[];
    chargeCurrencies: string[];
    upsells: Upsell[];
    emailDomains: string[];
    integrations: unknown[];
}

export interface CheckoutSession {
    id: string;
    checkoutToken: string;
    status: string;
    storeId: string;
    store: Store;
    accountId: string;
    customerId: string;
    draft: boolean;
    createdAt: string;
    updatedAt: string;
    lastActiveDate: string;
    selectedPresentmentCurrency: string;
    shippingRate?: { id: string; [key: string]: unknown } | null;
    shippingRateId?: string;
    sessionLineItems?: Array<{ isOrderBump?: boolean; [key: string]: unknown }>;
    customer?: {
        email?: string;
        firstName?: string;
        lastName?: string;
        currency?: string;
        [key: string]: unknown;
    };
    shippingAddress?: {
        firstName?: string;
        lastName?: string;
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        postal?: string;
        country?: string;
        phone?: string;
        [key: string]: unknown;
    };
    billingAddress?: {
        firstName?: string;
        lastName?: string;
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        postal?: string;
        country?: string;
        phone?: string;
        [key: string]: unknown;
    };
    metadata?: {
        cartCustomAttributes?: Array<{ name?: string; value?: string; [key: string]: unknown }>;
        clubProductId?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

export interface CheckoutSummaryItem {
    id: string;
    productId: string;
    variantId: string;
    priceId: string;
    product: {
        name: string;
        description: string;
    };
    variant: {
        name: string;
        description: string;
        imageUrl: string;
        grams: number;
    };
    sku: string;
    unitAmount: number;
    quantity: number;
    amount: number;
    adjustedAmount: number;
    currency: string;
    adjustments: CheckoutSessionPreviewAdjustment[];
    recurring: boolean;
    interval?: 'day' | 'week' | 'month' | 'year';
    intervalCount?: number;
    totalBillingCycles?: number;
    unitAmountAfterFirstCycle?: number;
    properties?: Record<string, unknown>;
    orderLineItemProduct: {
        name: string;
    };
    orderLineItemVariant: {
        name: string;
        imageUrl: string;
    };
    metadata: {
        originalIds: string[];
        isConsolidated: boolean;
    };
}

export interface CheckoutSummary {
    currency: string;
    items: CheckoutSummaryItem[];
    subtotalAmount: number;
    subtotalAdjustedAmount: number;
    shippingCost: number;
    shippingCostIsFree: boolean;
    totalAmount: number;
    totalAdjustedAmount: number;
    totalPromotionAmount: number;
    totalTaxAmount: number;
    lineItemsPromotionAmount: number;
    lineItemsTaxAmount: number;
    totalWeight: number;
    _consolidated: boolean;
    adjustments: {
        type: string;
        description: string;
        amount: number;
    }[];
}

export interface CheckoutSessionPreviewAdjustment {
    id?: string;
    type: 'Promotion' | 'Tax' | 'Shipping';
    description: string;
    level: 'Order' | 'LineItem';
    sourceId: string;
    amount: number;
    currency: string;
}

export interface CheckoutSessionPreviewItem {
    id: string;
    productId: string;
    product: {
        name: string;
        description: string;
    };
    variantId: string;
    variant: {
        name: string;
        description: string;
        imageUrl: string;
        grams: number;
    };
    priceId: string;
    sku: string;
    unitAmount: number;
    quantity: number;
    amount: number;
    adjustedAmount: number;
    currency: string;
    adjustments: CheckoutSessionPreviewAdjustment[];
    recurring?: boolean;
    rebillMode?: 'auth' | 'capture';
    rebillStepIntervalMs?: number;
    interval?: 'day' | 'week' | 'month' | 'year';
    intervalCount?: number;
    totalBillingCycles?: number;
    unitAmountAfterFirstCycle?: number;
    subscriptionSettings?: Record<string, unknown>;
    properties?: Record<string, unknown>;
    orderLineItemProduct: {
        name: string | null;
    } | null;
    orderLineItemVariant: {
        name: string | null;
        imageUrl: string | null;
    } | null;
}

export interface CheckoutSessionPreview {
    items: CheckoutSessionPreviewItem[];
    adjustments: CheckoutSessionPreviewAdjustment[];
    totalAmount: number;
    totalAdjustedAmount: number;
    totalPromotionAmount: number;
}

export interface PromotionAction {
    id: string;
    adjustmentAmount?: Record<string, { amount: number; [key: string]: unknown }>;
    adjustmentPercentage?: number;
    [key: string]: unknown;
}

export interface Promotion {
    id: string;
    name: string;
    description: string | null;
    type: string;
    rules: Record<string, unknown>[];
    actions: PromotionAction[];
}


export interface CheckoutData {
    checkoutSession: CheckoutSession;
    customerIsClubMember: boolean;
    clubProductId?: string;
    summary: CheckoutSummary;
    availablePromotions: Promotion[];
}

export class CheckoutResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Initialize a new checkout session (sync mode)
     * Response time: 2-5 seconds (full processing)
     */
    async initCheckout(params: CheckoutInitParams): Promise<{
        checkoutUrl: string;
        checkoutSession: CheckoutSession;
        checkoutToken: string;
    }> {
        // Pass all params including customerId to prevent duplicate customer creation
        return this.apiClient.post('/api/v1/checkout/session/init', params);
    }

    /**
     * Initialize a new checkout session (async mode) ⚡
     * Response time: ~50ms (20-50x faster!)
     * 
     * Returns checkoutToken immediately, background job completes processing.
     * Use getCheckout() to fetch full session data - it auto-waits for completion.
     * 
     * @example
     * // Fast init
     * const { checkoutToken } = await checkout.initCheckoutAsync(params);
     * 
     * // Redirect user immediately
     * window.location.href = `/checkout/${checkoutToken}/op`;
     * 
     * // By the time page loads, background processing is usually complete
     */
    async initCheckoutAsync(params: CheckoutInitParams): Promise<{
        checkoutToken: string;
        customerId: string;
        status: 'processing';
    }> {
        return this.apiClient.post('/api/v1/checkout/session/init-async', params);
    }

    /**
     * Preload checkout session (ultra-fast background pre-computation) ⚡⚡⚡
     * 
     * This is the recommended way to handle cart changes or "Buy Now" intent.
     * It pre-computes everything (checkoutToken, navigation URL, CMS session)
     * before the user even clicks the checkout button.
     * 
     * The SDK automatically gets funnelSessionId from FunnelClient if provided.
     * Only FunnelClient knows how to properly extract funnelSessionId (from state, URL, cookies, etc.)
     * 
     * @param params - Checkout and funnel parameters
     * @param getFunnelSessionId - Optional function to get funnelSessionId from FunnelClient
     *                             This maintains separation of concerns - only FunnelClient knows how to get it
     * 
     * @returns { checkoutToken, customerId, navigationUrl }
     */
    async preloadCheckout(
        params: CheckoutInitParams & {
            funnelSessionId?: string;
            currentUrl?: string;
            funnelStepId?: string;
            funnelVariantId?: string;
            navigationEvent?: string | { type: string; data?: Record<string, unknown> };
            navigationOptions?: Record<string, unknown>;
        },
        getFunnelSessionId?: () => string | null | undefined
    ): Promise<{
        checkoutToken: string;
        customerId: string;
        navigationUrl: string | null;
        funnelStepId?: string;
    }> {
        // ⚡ GET FUNNEL SESSION ID: Only FunnelClient knows how to properly get it
        // Priority: explicit param > FunnelClient > backend fallback (via currentUrl)
        let funnelSessionId = params.funnelSessionId;

        if (!funnelSessionId && getFunnelSessionId) {
            // Let FunnelClient handle extraction (from state, URL, cookies, etc.)
            funnelSessionId = getFunnelSessionId() || undefined;
        }

        // Format navigationEvent if it's a string
        const navigationEvent = typeof params.navigationEvent === 'string'
            ? { type: params.navigationEvent }
            : params.navigationEvent;

        // Build request - backend will also try to extract from currentUrl if not provided
        const requestParams = {
            ...params,
            ...(funnelSessionId && { funnelSessionId }),
            ...(navigationEvent && { navigationEvent }),
            // Ensure currentUrl is always set for backend extraction fallback
            currentUrl: params.currentUrl || (typeof window !== 'undefined' ? window.location.href : undefined),
        };

        return this.apiClient.post('/api/v1/checkout/session/preload', requestParams);
    }

    /**
     * Check async checkout processing status (instant, no waiting)
     * Perfect for polling or checking if background job completed
     */
    async checkAsyncStatus(checkoutToken: string): Promise<{
        isAsync: boolean;
        isProcessing: boolean;
        isComplete: boolean;
        hasError: boolean;
        error?: string;
    }> {
        return this.apiClient.get(`/api/public/v1/checkout/async-status/${checkoutToken}`);
    }

    /**
     * Get checkout session by token
     * 
     * SDK automatically waits for async completion (skipAsyncWait=false) for seamless UX.
     * This ensures async checkout sessions are fully processed before returning data.
     * 
     * If you need to skip waiting (e.g., for manual polling), use getCheckoutRaw() instead.
     */
    async getCheckout(checkoutToken: string, currency?: string): Promise<CheckoutData> {
        const queryParams = new URLSearchParams();
        if (currency) {
            queryParams.set('currency', currency);
        }
        // SDK explicitly waits for async completion (default is skip=true for retro compat)
        queryParams.set('skipAsyncWait', 'false');

        const url = `/api/v1/checkout-sessions/${checkoutToken}/v2${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        return this.apiClient.get(url);
    }

    /**
     * Get checkout session by token without waiting for async completion
     * Useful for manual polling scenarios
     */
    async getCheckoutRaw(checkoutToken: string, currency?: string): Promise<CheckoutData> {
        const queryParams = new URLSearchParams();
        if (currency) {
            queryParams.set('currency', currency);
        }
        queryParams.set('skipAsyncWait', 'true');

        const url = `/api/v1/checkout-sessions/${checkoutToken}/v2${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

        return this.apiClient.get(url);
    }

    /**
     * Update checkout address
     */
    async updateAddress(
        checkoutSessionId: string,
        data: {
            shippingAddress: Partial<OrderAddress>;
            billingAddress?: Partial<OrderAddress>;
        },
    ): Promise<{
        success: boolean;
        errors?: Record<string, { message: string; type: string }>;
        shippingCountryChanged: boolean;
        billingCountryChanged: boolean;
    }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/address`, {
            data,
        });
    }

    /**
     * Set checkout info
     */
    async setCheckoutInfo(
        checkoutSessionId: string,
        data: {
            shippingAddress: Partial<OrderAddress>;
            billingAddress?: Partial<OrderAddress>;
            differentBillingAddress?: boolean;
        },
    ): Promise<{
        success: boolean;
        errors?: Record<string, { message: string; type: string }>;
        shippingCountryChanged?: boolean;
        billingCountryChanged?: boolean;
    }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/checkout-info`, {
            shippingAddress: data.shippingAddress,
            billingAddress: data.differentBillingAddress ? data.billingAddress : data.shippingAddress,
            differentBillingAddress: data.differentBillingAddress || false,
        });
    }

    /**
     * Apply promotion code
     */
    async applyPromotionCode(
        checkoutSessionId: string,
        code: string,
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions/apply`, {
            code,
        });
    }

    /**
     * Remove promotion
     */
    async removePromotion(
        checkoutSessionId: string,
        promotionId: string,
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.delete(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions/${promotionId}`);
    }

    /**
     * Get applied promotions
     */
    async getAppliedPromotions(checkoutSessionId: string): Promise<Promotion[]> {
        return this.apiClient.get(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions`);
    }

    /**
     * Replace all session line items (full rewrite)
     */
    async replaceSessionLineItems(
        checkoutSessionId: string,
        lineItems: CheckoutLineItem[],
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items`, {
            lineItems,
        });
    }

    /**
     * Update line items
     */
    async updateLineItems(
        checkoutSessionId: string,
        lineItems: CheckoutLineItem[],
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items`, {
            lineItems,
        });
    }

    /**
     * Add line items
     */
    async addLineItems(
        checkoutSessionId: string,
        lineItems: CheckoutLineItem[],
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/add`, {
            lineItems,
        });
    }

    /**
     * Remove line items
     */
    async removeLineItems(
        checkoutSessionId: string,
        lineItems: { variantId: string; quantity?: number }[],
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/remove`, {
            lineItems,
        });
    }

    /**
     * Set item quantity
     */
    async setItemQuantity(
        checkoutSessionId: string,
        variantId: string,
        quantity: number,
        priceId?: string,
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/line-items/set-quantity`, {
            variantId,
            quantity,
            priceId,
        });
    }

    /**
     * Toggle order bump
     */
    async toggleOrderBump(
        checkoutSessionId: string,
        orderBumpOfferId: string,
        selected: boolean,
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/toggle-order-bump`, {
            orderBumpOfferId,
            selected,
        });
    }

    /**
     * Update customer
     */
    async updateCustomer(
        checkoutSessionId: string,
        data: {
            email: string;
            acceptsMarketing?: boolean;
        },
    ): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/customer`, data);
    }

    /**
     * Update customer and session info
     */
    async updateCustomerAndSessionInfo(
        checkoutSessionId: string,
        data: {
            customerData: {
                email: string;
                acceptsMarketing?: boolean;
            };
            shippingAddress: Partial<OrderAddress>;
            billingAddress?: Partial<OrderAddress>;
            differentBillingAddress?: boolean;
        },
    ): Promise<{
        success: boolean;
        errors?: Record<string, { message: string; type: string }>;
        shippingCountryChanged?: boolean;
        billingCountryChanged?: boolean;
    }> {
        return this.apiClient.post(`/api/v1/checkout-sessions/${checkoutSessionId}/customer-and-session-info`, data);
    }

    async previewCheckoutSession(params: {
        lineItems: CheckoutLineItem[];
        storeId?: string;
        currency: string;
        promotionIds?: string[];
    }): Promise<{
        success: boolean;
        preview: CheckoutSessionPreview;
    }> {
        return this.apiClient.post('/api/v1/checkout-sessions/preview', params);
    }

    /**
     * Set selected payment method for a checkout session
     */
    async setPaymentMethod(
        checkoutSessionId: string,
        paymentMethodName: PaymentMethodName,
    ): Promise<{ success: boolean }> {
        return this.apiClient.post(
            `/api/v1/checkout-sessions/${checkoutSessionId}/payment-method`,
            { paymentMethodName },
        );
    }
}
